import { isBedbankItem, isCruiseItem, isLEHotelItem, isTourV2Item } from 'lib/checkout/checkoutUtils'
import { OFFER_TYPE_BED_BANK } from 'constants/offer'
import getTourV2CountryCodesVisited from 'lib/tours/getTourV2CountryCodesVisited'
import { getCruiseCountriesByItinerary } from 'lib/cruises/cruiseUtils'

type LocationItem = App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem | App.Checkout.CruiseItem

function isLocationItem(item: App.Checkout.AnyItem): item is LocationItem {
  return isLEHotelItem(item) || isBedbankItem(item) || isTourV2Item(item) || isCruiseItem(item)
}

export function getOfferCountryCode(
  item: LocationItem,
  offer: App.AnyOffer | undefined,
): string | undefined {
  if (!offer) {
    return undefined
  }

  if (isTourV2Item(item)) {
    const itinerary = (offer as App.Tours.TourV2Offer).variations[item.purchasableOption.fkVariationId]?.itinerary
    const countryCodesVisited = itinerary ? getTourV2CountryCodesVisited(itinerary) : []
    if (countryCodesVisited.length === 0) {
      return undefined
    }
    if (countryCodesVisited.every(countryCode => countryCode === 'AU')) {
      return 'AU'
    }
    return countryCodesVisited.find(countryCode => countryCode !== 'AU')
  }

  if (isCruiseItem(item)) {
    const countryCodesVisited = getCruiseCountriesByItinerary((offer as App.CruiseOffer).itinerary)
    if (countryCodesVisited.length === 0) {
      return undefined
    }
    if (countryCodesVisited.every(countryCode => countryCode === 'AU')) {
      return 'AU'
    }
    return countryCodesVisited.find(countryCode => countryCode !== 'AU')
  }

  if (offer.type === OFFER_TYPE_BED_BANK) {
    return (offer as App.BedbankOffer).property?.address?.countryCode
  }

  return (offer as App.Offer).property?.geoData?.countryCode
}

function getLocationItems(items: Array<App.Checkout.AnyItem>): Array<LocationItem> {
  return items.filter(isLocationItem)
}

export function getCartOfferCountryCode(
  items: Array<App.Checkout.AnyItem>,
  offers: Record<string, App.AnyOffer | undefined>,
): string | undefined {
  const locationItems = getLocationItems(items)
  if (locationItems.length === 0) {
    return undefined
  }

  const offerIds = new Set(locationItems.map(item => item.offerId))
  if (offerIds.size !== 1) {
    return undefined
  }

  const codes = locationItems.map(item => getOfferCountryCode(item, offers[item.offerId]))
  const [firstCode] = codes
  if (!firstCode || codes.some(code => code !== firstCode)) {
    return undefined
  }

  return firstCode
}

export function isDomesticOrder(
  items: Array<App.Checkout.AnyItem>,
  offers: Record<string, App.AnyOffer | undefined>,
): boolean {
  return getCartOfferCountryCode(items, offers) === 'AU'
}
